import _ from 'lodash';
import { useEffect } from 'react';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { type SetStoreState, createStore } from '@/stores/createStore';
import type { BoardAppliedFilter } from '@/actionBoards/actionBoard.types';
import type { BoardOutputV1 } from '@/sdk/model/BoardOutputV1';

export interface ActionBoardState {
  boardId: string | undefined;
  board: BoardOutputV1 | undefined;
  tileRefreshQueue: { tileId?: string; token: number }[];
  startDate: string | undefined;
  endDate: string | undefined;
  appliedFilters: BoardAppliedFilter[] | null;
}

export type ActionBoardDehydratedState = Pick<ActionBoardState, 'boardId' | 'startDate' | 'endDate' | 'appliedFilters'>;

export interface ActionBoardActions {
  setBoardId: (boardId: string | undefined) => void;
  setBoard: (board: BoardOutputV1) => void;
  queueTileRefresh: (tileId?: string) => void;
  clearTileRefresh: (token: number) => void;
  setDateRange: (startDate: string | undefined, endDate: string | undefined) => void;
  setAppliedFilters: (appliedFilters: BoardAppliedFilter[] | null) => void;
  reset: (initialState: ActionBoardState) => void;
}

const buildInitialState = (): ActionBoardState => ({
  boardId: undefined,
  board: undefined,
  tileRefreshQueue: [],
  startDate: undefined,
  endDate: undefined,
  appliedFilters: null,
});

const actionBoardActionsFactory = (set: SetStoreState<ActionBoardState>): ActionBoardActions => ({
  setBoardId: (boardId) =>
    set((draft) => {
      draft.boardId = boardId;
    }),

  setBoard: (board) =>
    set((draft) => {
      draft.board = board;
      draft.boardId = board.id;
    }),

  queueTileRefresh: (tileId) =>
    set((draft) => {
      draft.tileRefreshQueue.push({ tileId, token: Date.now() + Math.random() });
    }),

  clearTileRefresh: (token) =>
    set((draft) => {
      const nextQueue = draft.tileRefreshQueue.filter((entry) => entry.token !== token);
      if (nextQueue.length !== draft.tileRefreshQueue.length) {
        draft.tileRefreshQueue = nextQueue;
      }
    }),

  setDateRange: (startDate, endDate) =>
    set((draft) => {
      draft.startDate = startDate;
      draft.endDate = endDate;
    }),

  setAppliedFilters: (appliedFilters) =>
    set((draft) => {
      draft.appliedFilters = appliedFilters;
    }),

  reset: (initialState) =>
    set((draft) => {
      Object.assign(draft, initialState);
    }),
});

export const dehydrateActionBoardState = (state: ActionBoardState): ActionBoardDehydratedState =>
  _.pick(state, ['boardId', 'startDate', 'endDate', 'appliedFilters']);

const [ActionBoardStoreProvider, useActionBoardStoreInstance, useActionBoardSelector] = createStore(
  'ActionBoard',
  buildInitialState(),
  actionBoardActionsFactory,
);

export { ActionBoardStoreProvider };

export const useActionBoardActions = () => useActionBoardStoreInstance().actions;
export const useActionBoardBoardId = () => useActionBoardSelector((state) => state.boardId);
export const useActionBoardBoard = () => useActionBoardSelector((state) => state.board);
export const useActionBoardTileRefreshQueue = () => useActionBoardSelector((state) => state.tileRefreshQueue);
export const useActionBoardStartDate = () => useActionBoardSelector((state) => state.startDate);
export const useActionBoardEndDate = () => useActionBoardSelector((state) => state.endDate);
export const useActionBoardAppliedFilters = () => useActionBoardSelector((state) => state.appliedFilters);

export const usePersistActionBoardStore = () => {
  const register = useRegisterPersistedStore();
  const store = useActionBoardStoreInstance();

  useEffect(() => {
    return register({
      storeName: 'sqActionBoardStore',
      persistenceLevel: 'WORKSHEET',
      store,
      dehydrate: dehydrateActionBoardState,
      reset: (initialState) => {
        store.setState(initialState, true);
      },
    });
  }, [register, store]);
};
